import { Alert, Card, Descriptions, Flex, Tag } from 'antd'
import dayjs from 'dayjs'

import { ConsultationTbScreening } from 'src/api/requests'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  screening: ConsultationTbScreening
}

const formatDate = (date?: string | null) =>
  date ? dayjs(date).format(dayMonthYearFormatWithDot) : undefined

/** Read-only TB screening of a consultation: answers and, when positive, the
 *  examination for active TB. */
export const DetailsTbScreeningCard = ({ screening }: Props) => {
  const { t } = useLocales()

  const yesNo = (answer?: boolean | null) =>
    answer === true ? (
      <Tag color="red">{t('common.yes')}</Tag>
    ) : answer === false ? (
      t('common.no')
    ) : (
      '-'
    )

  const withDate = (value?: string, date?: string | null) =>
    value ? [value, formatDate(date)].filter(Boolean).join(' — ') : '-'

  const workup = screening.workup

  return (
    <Card
      title={t('tb_screening.title')}
      extra={
        <Tag color={screening.isScreeningPositive ? 'orange' : 'green'}>
          {t(
            screening.isScreeningPositive
              ? 'tb_screening.screening_positive'
              : 'tb_screening.screening_negative'
          )}
        </Tag>
      }
    >
      <Flex vertical gap={16}>
        <Descriptions column={{ xs: 1, md: 2, lg: 3 }} size="small">
          <Descriptions.Item label={t('tb_screening.cough')}>
            {yesNo(screening.cough)}
          </Descriptions.Item>
          <Descriptions.Item label={t('tb_screening.fever')}>
            {yesNo(screening.fever)}
          </Descriptions.Item>
          <Descriptions.Item label={t('tb_screening.weight_loss')}>
            {yesNo(screening.weightLoss)}
          </Descriptions.Item>
          <Descriptions.Item label={t('tb_screening.night_sweats')}>
            {yesNo(screening.nightSweats)}
          </Descriptions.Item>
          <Descriptions.Item label={t('tb_screening.recent_tb_contact')}>
            {yesNo(screening.recentTbContact)}
          </Descriptions.Item>
          {screening.isScreeningPositive && (
            <Descriptions.Item label={t('tb_screening.is_hospitalized')}>
              {screening.isHospitalized ? t('common.yes') : t('common.no')}
            </Descriptions.Item>
          )}
        </Descriptions>

        {workup && (
          <Descriptions
            title={t('tb_screening.workup_title')}
            column={{ xs: 1, md: 2 }}
            size="small"
          >
            <Descriptions.Item label={t('tb_screening.crp')}>
              {withDate(
                workup.crpMgPerL !== null && workup.crpMgPerL !== undefined
                  ? `${workup.crpMgPerL} mg/l`
                  : undefined,
                workup.crpDate
              )}
              {workup.isCrpAboveThreshold && (
                <Tag color="red" style={{ marginInlineStart: 8 }}>
                  {t('tb_screening.crp_above')}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('tb_screening.chest_xray')}>
              {withDate(
                workup.chestXray
                  ? t(`tb_screening.chest_xray_result.${workup.chestXray}`)
                  : undefined,
                workup.chestXrayDate
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('tb_screening.lf_lam')}>
              {withDate(
                workup.lfLam
                  ? t(`tb_screening.test_result.${workup.lfLam}`)
                  : undefined,
                workup.lfLamDate
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('tb_screening.xpert')}>
              {withDate(
                workup.xpertMtbRif
                  ? t(`tb_screening.xpert_result.${workup.xpertMtbRif}`)
                  : undefined,
                workup.xpertMtbRifDate
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('tb_screening.referral')}>
              {workup.phthisiatricianReferral
                ? withDate(t('common.yes'), workup.phthisiatricianReferralDate)
                : t('common.no')}
            </Descriptions.Item>
            {workup.phthisiatricianReferralNotes && (
              <Descriptions.Item label={t('tb_screening.referral_notes')}>
                {workup.phthisiatricianReferralNotes}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        {screening.isTbPositive && (
          <Alert
            type="error"
            showIcon
            message={t('tb_screening.tb_positive_alert')}
          />
        )}
      </Flex>
    </Card>
  )
}
